import { inject, Injectable } from '@angular/core';
import { combineLatest, distinctUntilChanged, filter, map, Observable, of, switchMap } from 'rxjs';
import { freeStripeProductPrice, getBytesFromGigabytes, getBytesFromTerabytes } from '../billing-consts';
import { GbForStoragePrice } from '../consts';
import { GROWTHBOOK } from '../growthbook';
import { findSubscriptionLineItem, isFreeSubscription } from '../lib/subscription-helpers';
import { getTenantActiveStorage } from '../lib/tenant-active-storage';
import { PermissionService } from './permission.service';
import { StateService } from './state.service';
import { SubscriptionService } from './subscription.service';

export interface StorageInfo {
  total: bigint;
  plan: bigint;
  addOn: bigint;
  used: bigint;
}

@Injectable({
  providedIn: 'root',
})
export class TenantStorageService {
  private permissionService = inject(PermissionService);
  private state = inject(StateService);
  private subscriptionService = inject(SubscriptionService);
  private growthBook = inject(GROWTHBOOK);

  public getStorage(): Observable<StorageInfo> {
    if (this.state.visitingTenant && this.growthBook.isOn('public_upload')) {
      return of({
        total: getBytesFromTerabytes(10240n), // todo: get from backend
        used: 0n,
        plan: getBytesFromTerabytes(10240n),
        addOn: getBytesFromTerabytes(10240n),
      });
    }
    return combineLatest([
      this.state.activeTenant$.pipe(
        distinctUntilChanged(
          (a, b) => a?.id === b?.id && a?.storage_size === b?.storage_size && a?.active_storage === b?.active_storage
        )
      ),
      this.state.sharedWithMeActiveTenant$.pipe(
        distinctUntilChanged(
          (a, b) => a?.id === b?.id && a?.storage_size === b?.storage_size && a?.active_storage === b?.active_storage
        )
      ),
    ]).pipe(
      filter(([tenant, sharedWithMeTenant]) => !!tenant || !!sharedWithMeTenant),
      switchMap(([tenant, sharedWithMeTenant]) => {
        if (sharedWithMeTenant) {
          return of({
            total: getBytesFromTerabytes(1024n), // todo: get from backend
            used: getTenantActiveStorage(sharedWithMeTenant, this.growthBook.isOn('new_storage_size_calculation')),
            plan: getBytesFromTerabytes(1024n),
            addOn: getBytesFromTerabytes(1024n),
          });
        }

        return combineLatest([
          this.subscriptionService.getSubscription(),
          this.permissionService.getTenantBillingPermissions(tenant.id),
        ]).pipe(
          map(([subscription, permissions]) => {
            if (isFreeSubscription(subscription)) {
              const storage = getBytesFromGigabytes(BigInt(freeStripeProductPrice.metadata.storage_capacity));
              return {
                total: storage,
                plan: storage,
                addOn: 0n,
                used: getTenantActiveStorage(tenant, this.growthBook.isOn('new_storage_size_calculation')),
              };
            }

            const storageAddon = findSubscriptionLineItem(subscription.items.data, 'storage_addon');

            const planStorageTotal = BigInt(permissions.storage_plan_limit);
            const storageAddonTotal = storageAddon
              ? BigInt(GbForStoragePrice(storageAddon.price) * storageAddon.quantity) *
                BigInt(1024) *
                BigInt(1024) *
                BigInt(1024)
              : 0n;

            return {
              total: planStorageTotal + storageAddonTotal,
              plan: planStorageTotal,
              addOn: storageAddonTotal,
              used: getTenantActiveStorage(tenant, this.growthBook.isOn('new_storage_size_calculation')),
            };
          })
        );
      })
    );
  }
}
